import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  colors,
  hairline,
  screenPadding,
  spacing,
  useResponsive,
} from '../../theme';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

/**
 * The strip every account page opens with: back, a title, one optional action.
 *
 * Shared rather than repeated on five screens, so the arrow, the title and the
 * action can never drift apart between them - the thing that makes a set of
 * pages feel like one app.
 *
 * The title centres by giving the arrow and the action equal flex. An
 * absolutely-positioned centre would sit on top of a long action label instead
 * of pushing it, and "Mark all read" is long.
 */

type Props = {
  title: string;
  onBack: () => void;
  /** Right-hand control - "+ Add New", "Mark all read". Omitted for none. */
  action?: string;
  onAction?: () => void;
  /**
   * Paints the strip the canvas colour instead of white, and drops its
   * bottom rule.
   *
   * For a page that opens on artwork rather than on a list. The default white
   * strip is a deliberate edge against the grey canvas below it - but above a
   * pale banner that edge becomes a seam across the top of the illustration,
   * two nearly-equal light tones divided by a line. Flush, the header reads as
   * part of the banner, which is what a page fronted by art wants.
   *
   * Opt-in, so the five account pages that came first are untouched.
   */
  flush?: boolean;
};

function PageHeader({ title, onBack, action, onAction, flush }: Props) {
  /*
   * The strip spans the full width on every device - it is chrome, and chrome
   * that stops short of the edges reads as a floating card. What moves is
   * where its contents sit: on a wide screen the page below is a centred
   * column, and a back arrow out at the far edge would have nothing beneath
   * it to belong to. Zero on a phone, so nothing changes there.
   */
  const { sideInset } = useResponsive();

  /*
   * The status bar's height, added to this strip's own top padding.
   *
   * ---------------------------------------------------------------------------
   * WHY THE HEADER OWNS THIS AND NOT ITS PARENT
   * ---------------------------------------------------------------------------
   * Every account and support page renders inside `HomeScreen`'s overlay -
   * `styles.addingAddress`, which is `position: 'absolute'` pinned to `top: 0`.
   * Absolute positioning is measured from the SafeAreaView's OUTER edge, above
   * the top inset, so the strip was laid out underneath the clock and the
   * battery: the title and the back arrow were drawn, and the status bar was
   * painted on top of them. That is the "header is gone inside the screen" on
   * Settings, Help & Support, Referrals and every other page in that overlay.
   *
   * Fixing it here rather than in the overlay is deliberate: one change covers
   * every page that uses this strip, on all of its mount paths, instead of
   * being reapplied per parent and missed on the next one added.
   *
   * SAFE FROM DOUBLE-PADDING, but by audit rather than by magic -
   * `useSafeAreaInsets` reports the DEVICE inset and does not know what a
   * parent has already applied. Every screen that renders this header was
   * checked: none wraps it in a `SafeAreaView` carrying the `top` edge, and
   * those mounted in `HomeScreen`'s absolute overlay are inset by nothing at
   * all, which is the bug above. Give a future screen its own top-inset
   * wrapper and this must be revisited.
   */
  const insets = useSafeAreaInsets();

  return (
    <View
      style={[
        styles.header,
        {
          paddingHorizontal: screenPadding + sideInset,
          paddingTop: spacing.lg + insets.top,
        },
        flush ? styles.headerFlush : null,
      ]}
    >
      <View style={styles.side}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back"
          onPress={onBack}
          hitSlop={12}
          // The glyph's ink sits high in its box, so centring the box alone
          // still left the arrow reading above the title beside it.
          style={styles.back}
        >
          <Icon name="arrowLeft" size={24} color={colors.ink} strokeWidth={2} />
        </Pressable>
      </View>

      <Text variant="title" align="center" style={styles.title} numberOfLines={1}>
        {title}
      </Text>

      <View style={[styles.side, styles.sideEnd]}>
        {action ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={action}
            onPress={onAction}
            hitSlop={10}
            style={({ pressed }) => [
              styles.action,
              pressed ? styles.pressed : null,
            ]}
          >
            <Text variant="labelSm" color={colors.primary} numberOfLines={1}>
              {action}
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    backgroundColor: colors.surface,
    borderBottomWidth: hairline.borderWidth,
    borderBottomColor: hairline.borderColor,
  },
  headerFlush: {
    backgroundColor: colors.background,
    borderBottomWidth: 0,
  },
  /* Equal flanks are what centre the title: each claims the same width whatever
     is inside it, so the title lands on the strip's true middle. */
  side: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  sideEnd: {
    justifyContent: 'flex-end',
  },
  back: {
    marginTop: 3,
  },
  /*
   * The weight is spelled out rather than left to the variant: `title` asks for
   * Satoshi-Bold, `assets/fonts/` is empty, so the family resolves to nothing
   * and the text would come back at regular weight.
   */
  title: {
    fontWeight: '700',
  },
  action: {
    paddingVertical: spacing.xs,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default PageHeader;
